import { css, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map';

import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { booleanConverter } from '../common/converters';
import { InputSkin } from '../common/component-types';

import '../te-text';
import '../te-icon';
import '../te-loader';

type AcceptedInputTypes = 'text' | 'number' | 'password';

export class TeInputComponent extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        position: relative;
      }

      .icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0px;
        left: 0;
        z-index: 1;
        width: 40px;
        height: 40px;
        pointer-events: none;
      }

      .te-input {
        margin: 0;
        padding: 0;
        width: fit-content;
        position: relative;
      }

      input {
        border-radius: 4px;
        border: 0;
        color: ${theme.colors.text};
        font-size: 14px;
        height: 40px;
        outline: none;
        padding-left: 16px;
        margin: 0;
        -webkit-appearance: none;
      }

      input::placeholder {
        color: ${theme.colors.shade4};
        font-style: italic;
      }

      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      input:focus {
        box-shadow: 0 0 0 2px ${theme.colors.primary.v0}1A;
      }

      input:disabled,
      input:disabled::placeholder {
        color: ${theme.colors.shade3};
      }

      .loader {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 40px;
        right: 0;
        top: 0;
      }

      .error,
      .count {
        margin: 8px 16px 0;
        top: 100%;
      }
    `,
  ];

  firstUpdated() {
    if (this.maxLength) this.charEntered = this.value.length;
  }

  updated(changedProps: Map<string, unknown>) {
    const input = this.shadowRoot?.querySelector('input');
    if (!input) return;
    if (!changedProps.has('value') || !this.maxLength) return;
    this.charEntered = input.value.length;
  }
  /** Value of input */
  @property({ type: String, attribute: 'value' }) value = '';
  /** Input is disabled */
  @property({ type: Boolean, converter: booleanConverter }) disabled = false;
  /** Input is in loading state */
  @property({ type: Boolean, converter: booleanConverter }) loading = false;
  /** Input has error */
  @property({ type: Boolean, converter: booleanConverter }) hasError = false;
  /** Placement of the label */
  @property({ type: String }) labelPlacement: 'top' | 'left' | 'right' = 'top';
  /** Basic color scheme of input */
  @property({ type: String }) skin: InputSkin = 'default';
  /** Text for label */
  @property({ type: String }) label = '';
  /** Maximum characters allowed for input */
  @property({ type: Number }) maxLength?: number;
  /** Text to display if there is error */
  @property({ type: String }) errorText = 'Please insert a value';
  /** Text before any value is received */
  @property({ type: String }) placeholderText = 'Placeholder text';
  /** Minimum width for input */
  @property({ type: Number }) minWidth = 384;
  /** Maximum width for input */
  @property({ type: Number }) maxWidth?: number;
  /** Content alignment inside input */
  @property({ type: String }) align?: 'center' | 'right' | 'left';

  @state() charEntered = 0;

  getInputStyle() {
    this.minWidth = Math.min(this.minWidth, this.maxWidth || this.minWidth);
    const hasErrorColor = this.hasError || this.charEntered > (this.maxLength || this.charEntered);
    return {
      background: this.skin === 'gray' ? `${theme.colors.shade2}` : `${theme.colors.shade0}`,
      opacity: this.disabled ? '0.7' : '1',
      border: hasErrorColor
        ? `1px solid ${theme.colors.inputError}33`
        : `1px solid ${theme.colors.shade2}`,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      minWidth: `${this.minWidth}px`,
      maxWidth: this.maxWidth ? `${this.maxWidth}px` : 'fit-content',
      paddingRight: this.loading ? '40px' : '16px',
      textAlign: this.align || 'unset',
    };
  }

  handleInputKeydown(e: KeyboardEvent, type: string) {
    e.stopPropagation();
    const input = this.shadowRoot?.querySelector('input');
    if (!input) return;
    if (type === 'number' && ['ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault();

    if (['Backspace', 'Delete'].includes(e.key)) {
      this.charEntered = input.value.length;
    }
    if (e.key === 'Enter') {
      input.value = this.value || input.value;
    }
    this.handleKeyDown(e);
  }

  getWrapperStyle() {
    const { labelPlacement, label } = this;
    return {
      display: label ? 'flex' : 'unset',
      gap: label ? '8px' : 'unset',
      flexDirection: { top: 'column', left: 'row', right: 'row-reverse' }[labelPlacement],
      alignItems: labelPlacement === 'top' ? 'flex-start' : 'center',
    };
  }

  renderLabel() {
    return this.label
      ? html`
          <te-text class="label" weight="700">
            ${this.label}
          </te-text>
        `
      : null;
  }

  renderLoader() {
    return this.loading
      ? html`
          <te-loader class="loader" size="16"></te-loader>
        `
      : null;
  }

  renderCharCount() {
    return this.maxLength
      ? html`
          <te-text
            class="count"
            color=${this.charEntered > this.maxLength
              ? theme.colors.inputError.toString()
              : theme.colors.shade4.toString()}
            italic
          >
            ${this.charEntered} of ${this.maxLength}
          </te-text>
        `
      : null;
  }

  renderError() {
    return this.hasError
      ? html`
          <te-text class="error" color=${theme.colors.inputError.toString()}>
            ${this.errorText}
          </te-text>
        `
      : null;
  }

  renderInput(
    inputType: AcceptedInputTypes,
    isPassword?: boolean,
    minValue?: number,
    maxValue?: number
  ) {
    const isPlaceholder = inputType === 'text' && !isPassword;
    return html`
      <input
        .placeholder=${isPlaceholder ? this.placeholderText : ''}
        type=${inputType}
        id="${this.id}"
        .value=${this.value}
        @keyup=${this.handleChange}
        @keydown=${(e: KeyboardEvent) => this.handleInputKeydown(e, inputType)}
        @click=${this.handleClick}
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
        style=${styleMap(this.getInputStyle())}
        ?disabled=${this.disabled}
        maxlength=${this.maxLength || Infinity}
        min=${typeof minValue === 'number' ? minValue : -Infinity}
        max=${typeof maxValue === 'number' ? maxValue : Infinity}
      />
    `;
  }
}
